import {
	type ActorRefFrom,
	assign,
	sendTo,
	setup,
	type StateFrom,
} from 'xstate';

import type { Cart, GiftCard } from 'models/api';
import type { DiscountCodeNumber } from 'types';

import type {
	GiftCardBonusInput,
	GiftCardBonusMachineContext,
	GiftCardBonusMachineEvents,
	GiftCardPinId,
} from './giftCardBonus.types';

export const giftCardBonusMachine = setup({
	// oxlint-disable-next-line typescript/no-unnecessary-type-assertion
	types: {} as {
		context: GiftCardBonusMachineContext;
		events: GiftCardBonusMachineEvents;
		input: GiftCardBonusInput;
	},
	actions: {
		updateData: assign({
			cart: (_, params: { cart: Cart | undefined }) => params.cart,
			giftCards: (_, params) => params.cart?.giftCards ?? [],
			bonusDiscounts: (_, params) => params.cart?.bonusDiscounts ?? [],
			availableBonusDiscounts: (_, params) =>
				params.cart?.availableBonusDiscounts ?? [],
			errorList: (_, params) => params.cart?.errorList ?? [],
		}),

		sendAddRemoveGiftCard: sendTo(
			({ context }) => context.checkoutMachineRef,
			(
				{ context },
				params: { giftCard: GiftCardPinId; variant: 'add' | 'remove' },
			) => {
				let updatedGiftCards: GiftCard[] = [];
				if (params.variant === 'add') {
					updatedGiftCards = [...context.giftCards, params.giftCard];
				} else if (params.variant === 'remove') {
					updatedGiftCards = context.giftCards.filter(
						(giftCard) => giftCard.id !== params.giftCard.id,
					);
				}
				return {
					type: 'UPDATE_CART',
					updatedCartProperties: { giftCards: updatedGiftCards },
				};
			},
		),
		optimisticUpdateBonusDiscounts: assign({
			bonusDiscounts: (
				{ context },
				params: { bonusChequeId: DiscountCodeNumber },
			) => {
				if (
					context.bonusDiscounts.some(
						(d) => d.checkNumber === params.bonusChequeId,
					)
				) {
					return context.bonusDiscounts.filter(
						(d) => d.checkNumber !== params.bonusChequeId,
					);
				}
				return [
					...context.bonusDiscounts,
					{ checkNumber: params.bonusChequeId },
				];
			},
		}),
		saveUpdatedBonusChequeIdToContext: assign({
			updatedBonusChequeId: (
				_,
				params: { bonusChequeId: DiscountCodeNumber },
			) => params.bonusChequeId,
		}),
		sendAddRemoveBonusCheque: sendTo(
			({ context }) => context.checkoutMachineRef,
			({ context }) => ({
				type: 'UPDATE_CART',
				updatedCartProperties: {
					bonusChequeId: context.updatedBonusChequeId,
				},
			}),
		),
	},
}).createMachine({
	id: 'giftCard',
	initial: 'idle',

	context: ({ input: { checkoutMachineRef } }) => ({
		giftCards: [],
		errorList: [],
		availableBonusDiscounts: [],
		bonusDiscounts: [],
		checkoutMachineRef,
	}),
	states: {
		idle: {
			on: {
				CART_UPDATED: {
					target: 'idle',
					actions: {
						type: 'updateData',
						params: ({ event }) => ({ cart: event.value }),
					},
				},
				ADD_REMOVE_GIFT_CARD: {
					target: 'loading',
					actions: {
						type: 'sendAddRemoveGiftCard',
						params: ({ event }) => ({
							giftCard: event.giftCard,
							variant: event.variant,
						}),
					},
				},
			},
		},
		debounceUpdateBonusCheck: {
			after: {
				250: {
					target: 'loading',
					actions: ['sendAddRemoveBonusCheque'],
				},
			},
		},
		loading: {
			on: {
				CART_UPDATED: {
					target: 'idle',
					actions: {
						type: 'updateData',
						params: ({ event }) => ({ cart: event.value }),
					},
				},
			},
		},
	},
	on: {
		ADD_REMOVE_BONUS_CHECK: {
			target: '.debounceUpdateBonusCheck',
			reenter: true,
			actions: [
				{
					type: 'optimisticUpdateBonusDiscounts',
					params: ({ event }) => ({
						bonusChequeId: event.bonusChequeId,
					}),
				},
				{
					type: 'saveUpdatedBonusChequeIdToContext',
					params: ({ event }) => ({
						bonusChequeId: event.bonusChequeId,
					}),
				},
			],
		},
	},
});
export type GiftCardBonusMachineState = StateFrom<typeof giftCardBonusMachine>;
export type GiftCardBonusMachineActor = ActorRefFrom<
	typeof giftCardBonusMachine
>;
